import React, { useEffect, useRef, useState } from 'react';
import { useIdCardTemplate } from '../../hooks/useIdCardTemplate';
import { computeFitScale } from '../../utils/idCardScale';

function CardPreview({ html, w, h, label, previewKey, showLabels = true, maxWidth = 430, maxHeight = 645 }) {
  const measureRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(maxWidth);

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return undefined;

    const update = () => {
      const width = el.clientWidth;
      if (width > 0) {
        setContainerWidth(Math.min(maxWidth, width));
      }
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxWidth]);

  const effectiveMaxW = containerWidth > 0 ? containerWidth : maxWidth;
  const { scale, scaledWidthPx, scaledHeightPx } = computeFitScale(w, h, effectiveMaxW, maxHeight);

  return (
    <div ref={measureRef} className="group flex w-full max-w-[430px] flex-col items-center gap-3">
      {showLabels && (
        <span className="text-[11px] font-semibold uppercase tracking-widest text-orange-700/80 dark:text-orange-300/90">
          {label}
        </span>
      )}
      <div
        className="relative mx-auto rounded-xl overflow-hidden bg-white shadow-[0_12px_40px_rgba(242,92,5,0.18)] ring-1 ring-orange-200/60 transition-transform duration-300 md:group-hover:scale-[1.02]"
        style={{ width: scaledWidthPx, height: scaledHeightPx }}
      >
        <div
          key={previewKey}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: w,
            height: h,
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

/**
 * Master ID card template — same layout as Student Profile ID tab.
 */
export default function StudentIDCardTemplate({
  student,
  school,
  photoUrl,
  imageCacheKey = '',
  assetOverrides = null,
  previewVersion = 0,
  format = 'portrait',
  design = 'saffron',
  compact = false,
  showLabels = true,
}) {
  const {
    frontHtml,
    backHtml,
    size,
    assetsReady,
    previewKey,
  } = useIdCardTemplate({
    student,
    school,
    photoUrl,
    imageCacheKey,
    assetOverrides,
    previewVersion,
    format,
    design,
  });

  return (
    <div
      className={
        compact
          ? 'flex w-full flex-wrap justify-center gap-8 overflow-x-hidden py-4'
          : 'flex w-full flex-col flex-wrap items-center justify-center gap-10 overflow-x-hidden rounded-2xl border border-orange-200/50 bg-gradient-to-br from-orange-50 via-white to-orange-50/40 px-2 py-8 dark:border-orange-900/40 dark:from-orange-950/30 dark:via-background dark:to-orange-950/20 sm:px-4 xl:flex-row xl:gap-14'
      }
    >
      {!assetsReady ? (
        <div className="flex w-full justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
        </div>
      ) : (
        <>
          <CardPreview
            html={frontHtml}
            w={size.widthPx}
            h={size.heightPx}
            label="Front"
            previewKey={`f-${previewKey}`}
            showLabels={showLabels}
          />
          <CardPreview
            html={backHtml}
            w={size.widthPx}
            h={size.heightPx}
            label="Back"
            previewKey={`b-${previewKey}`}
            showLabels={showLabels}
          />
        </>
      )}
    </div>
  );
}

export { CardPreview };
